import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScoreStatusClient, ScoreStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new ScoreStatusClient(API_BASE_URL, axiosInstance);

export const useScoreStatuses = () =>
  useQuery({
    queryKey: ['scoreStatuses'],
    queryFn: async () => {
      const response = await getApiClient().searchScoreStatuses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useScoreStatusSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchScoreStatuses(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useScoreStatusById = (id: string | undefined) =>
  useQuery({
    queryKey: ['scoreStatus', id],
    queryFn: async () => {
      const response = await getApiClient().getScoreStatusById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateScoreStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScoreStatusDTO) => getApiClient().createScoreStatus(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scoreStatuses'] }),
  });
};

export const useUpdateScoreStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScoreStatusDTO }) =>
      getApiClient().updateScoreStatus(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scoreStatuses'] }),
  });
};

export const useDeleteScoreStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteScoreStatus(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scoreStatuses'] }),
  });
};